import { useState } from 'react'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Alert,
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	Stack,
	Typography,
} from '@mui/material'

function technicalDiagnostics(diagnostics) {
	return [
		...(diagnostics?.diagnostics || []),
		...(diagnostics?.blockingListings || []).flatMap(
			(listing) => listing.diagnostics || []
		),
	]
}

function AvitoAutoloadSettingsDialog({
	open,
	onClose,
	hostedFeed,
	busy,
	diagnostics,
	feedPreview,
	oldUrlWarning,
	onCopy,
	onOpenGuide,
	onOpenXml,
	onDownload,
	onLoadDiagnostics,
	onRotate,
}) {
	const [rotationOpen, setRotationOpen] = useState(false)
	const rawDiagnostics = technicalDiagnostics(diagnostics)

	return (
		<>
			<Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
				<DialogTitle>Настройки автозагрузки</DialogTitle>
				<DialogContent>
					<Stack spacing={3} sx={{ pt: 1 }}>
						<Box>
							<Typography variant="h6">Связь с Авито</Typography>
							<Typography color="text.secondary">
								Эту ссылку нужно один раз указать в настройках автозагрузки
								Авито.
							</Typography>
						</Box>
						{hostedFeed?.ready ? (
							<Stack spacing={1}>
								<Typography variant="subtitle2">Ссылка для автозагрузки</Typography>
								<Typography
									component="div"
									sx={{ wordBreak: 'break-all', fontFamily: 'monospace' }}>
									{hostedFeed.publicUrl}
								</Typography>
								<Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
									<Button variant="contained" onClick={onCopy} disabled={busy}>
										Скопировать
									</Button>
									<Button onClick={onOpenGuide}>Как настроить в Авито</Button>
								</Stack>
							</Stack>
						) : (
							<Alert severity="info">
								Ссылка станет доступна после первой успешной подготовки данных.
							</Alert>
						)}
						{oldUrlWarning && <Alert severity="warning">{oldUrlWarning}</Alert>}

						<Accordion disableGutters elevation={0} variant="outlined">
							<AccordionSummary expandIcon={<ExpandMoreIcon />}>
								<Typography>Дополнительно</Typography>
							</AccordionSummary>
							<AccordionDetails>
								<Stack spacing={2}>
									<Typography color="text.secondary">
										Технические функции для диагностики и поддержки.
									</Typography>
									<Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
										<Button
											variant="outlined"
											onClick={onOpenXml}
											disabled={!hostedFeed?.ready || busy}>
											Открыть XML
										</Button>
										<Button
											variant="outlined"
											onClick={onDownload}
											disabled={!hostedFeed?.ready || busy}>
											Скачать XML
										</Button>
										<Button onClick={onLoadDiagnostics} disabled={busy}>
											Обновить диагностику
										</Button>
									</Stack>
									{hostedFeed?.configured && (
										<Typography variant="caption" color="text.secondary">
											Состояние: {hostedFeed.state}. В автозагрузке:{' '}
											{hostedFeed.membershipCount || 0}. В текущей версии:{' '}
											{hostedFeed.snapshotCount || 0}.
										</Typography>
									)}
									{feedPreview && (
										<Alert severity={feedPreview.valid ? 'success' : 'warning'}>
											{feedPreview.valid
												? `Техническая проверка пройдена: ${feedPreview.count} объявлений.`
												: 'Техническая проверка обнаружила проблемы.'}
										</Alert>
									)}
									{rawDiagnostics.length > 0 && (
										<Box component="ul" sx={{ my: 0, pl: 3 }}>
											{rawDiagnostics.map((item, index) => (
												<li key={`${item.code || 'diagnostic'}-${index}`}>
													<Typography variant="caption">
														{item.code || 'AVITO_FEED_DIAGNOSTIC'}
													</Typography>
												</li>
											))}
										</Box>
									)}
								</Stack>
							</AccordionDetails>
						</Accordion>

						{hostedFeed?.configured && (
							<>
								<Divider />
								<Box>
									<Typography variant="h6" color="error">
										Опасные действия
									</Typography>
									<Typography color="text.secondary" sx={{ mb: 1 }}>
										Используйте только если текущая ссылка стала недоступна посторонним.
									</Typography>
									<Button
										color="error"
										variant="outlined"
										onClick={() => setRotationOpen(true)}
										disabled={busy}>
										Создать новую ссылку
									</Button>
								</Box>
							</>
						)}
					</Stack>
				</DialogContent>
				<DialogActions>
					<Button onClick={onClose} disabled={busy}>
						Закрыть
					</Button>
				</DialogActions>
			</Dialog>

			<Dialog
				open={rotationOpen}
				onClose={busy ? undefined : () => setRotationOpen(false)}
				fullWidth
				maxWidth="xs">
				<DialogTitle>Создать новую ссылку?</DialogTitle>
				<DialogContent>
					<Alert severity="warning">
						Старая ссылка перестанет работать. После этого новую ссылку
						потребуется заменить в настройках Авито.
					</Alert>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setRotationOpen(false)} disabled={busy}>
						Отмена
					</Button>
					<Button
						color="error"
						variant="contained"
						disabled={busy}
						onClick={async () => {
							await onRotate()
							setRotationOpen(false)
						}}>
						Создать новую ссылку
					</Button>
				</DialogActions>
			</Dialog>
		</>
	)
}

export default AvitoAutoloadSettingsDialog
